import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { MatCheckbox, MatCheckboxChange } from '@angular/material/checkbox';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiV1Service, AssessmentSummary } from 'api';
import { RevisionWithAncestors } from 'component/details/requirement-intake/revision-resolver';
import { combineLatest, firstValueFrom, map, tap } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonDirective } from 'common/button/button.directive';

@Component({
  templateUrl: 'self-declaration.component.html',
  styleUrl: 'self-declaration.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatCheckbox, TranslateModule, RouterLink, ButtonDirective],
})
export class SelfDeclarationComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly apiService = inject(ApiV1Service);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);

  protected readonly revision = signal<RevisionWithAncestors | undefined>(undefined);
  protected readonly agreementChecked = signal(false);
  protected readonly isLoading = signal(true);
  protected readonly isSubmitting = signal(false);
  protected readonly errorMessage = signal<string | undefined>(undefined);
  protected readonly assessmentSummary = signal<AssessmentSummary | undefined>(undefined);
  protected readonly embedded = computed(() => {
    const embedded = this.queryParams().get('embedded') ?? 'false';
    return embedded === 'true';
  });
  protected readonly canSubmit = computed(
    () => !this.isLoading() && !this.isSubmitting() && this.agreementChecked(),
  );

  private readonly queryParams = toSignal(this.route.queryParamMap, {
    initialValue: this.route.snapshot.queryParamMap,
  });

  constructor() {
    const parentRoute = this.route.parent;

    if (!parentRoute) {
      this.isLoading.set(false);
      this.errorMessage.set(
        this.translateService.instant('selfDeclaration.errors.unableToLoadRevisionContext'),
      );
      return;
    }

    combineLatest([parentRoute.paramMap, parentRoute.data])
      .pipe(
        map(([params, data]) => ({
          revisionId: Number(params.get('revisionId')),
          revision: data['Revision'] as RevisionWithAncestors | undefined,
        })),
        tap(({ revisionId, revision }) => {
          this.revision.set(revision);
          this.errorMessage.set(undefined);
          this.agreementChecked.set(false);

          if (!Number.isInteger(revisionId)) {
            this.isLoading.set(false);
            this.errorMessage.set(
              this.translateService.instant('selfDeclaration.errors.unableToResolveRevision'),
            );
            return;
          }

          void this.loadAnalysisState(revisionId);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  protected onAgreementChange(event: MatCheckboxChange): void {
    this.agreementChecked.set(event.checked);
  }

  protected async onCancel(): Promise<void> {
    await this.router.navigate(['..'], { relativeTo: this.route });
  }

  protected async onSubmitDeclaration(): Promise<void> {
    const currentRevision = this.revision();

    if (!currentRevision || this.isSubmitting() || !this.agreementChecked()) {
      return;
    }

    this.isSubmitting.set(true);
    this.errorMessage.set(undefined);

    try {
      await firstValueFrom(this.apiService.submitDeclaration(currentRevision.id));

      this.revision.set({
        ...currentRevision,
        declarationStatus: 'VALID',
        declarationDateUtc: new Date().toISOString(),
      });
      this.agreementChecked.set(false);
      await this.router.navigate(['..', 'declared-compliant'], { relativeTo: this.route });
    } catch {
      this.errorMessage.set(this.translateService.instant('selfDeclaration.errors.submitFailed'));
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async loadAnalysisState(revisionId: number): Promise<void> {
    this.isLoading.set(true);

    try {
      this.assessmentSummary.set(
        await firstValueFrom(this.apiService.getAssessmentSummary(revisionId)),
      );
      this.errorMessage.set(undefined);
    } catch {
      this.errorMessage.set(
        this.translateService.instant('selfDeclaration.errors.loadAssessmentSummaryFailed'),
      );
    } finally {
      this.isLoading.set(false);
    }
  }
}
